import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import TableFilter from "src/components/TableFilter";
import CommonService from "src/modules/data-protection/apis/CommonService";

const FilteringComponent = ({ t, lng, onChange }) => {
  const [tableFilters, setTableFilters] = useState({
    analyst: undefined,
    q: undefined, // Text
    legal_entity_ids: undefined,
    status: undefined,
    unassigned: undefined,
    revision_overdue: undefined,
    next_revision: undefined,
    next_revision_from: undefined, // API value
    next_revision_to: undefined, // API value
  });

  const yesNoOptions = useMemo(() => {
    return [
      { id: true, title: t("Yes") },
      { id: false, title: t("No") },
    ];
  }, [t]);

  const { data: legalEntities, isFetching: entitiesAreFetching } = useQuery({
    queryKey: ["data-protection-fetch-all-legal-entities-list", lng],
    queryFn: async () => {
      const service = CommonService.getInstance();
      return service.fetchAllLegalEntities();
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: () => {
      toast(t("An error occurred while fetching entities."), {
        type: "error",
      });
    },
  });


  const { data: analysts, isFetching: analystsAreFetching } = useQuery({
    queryKey: ["gdpr-fetch-analysts-admins"],
    queryFn: async () => {
      const service = CommonService.getInstance();

      return await service.FetchAnalystsAndAnalystAdmins();
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: () => {
      toast(t("An error occurred while fetching owners."), {
        type: "error",
      });
    },
  });

  const statuses = useMemo(() => {
    return [
      { value: "IN_PROGRESS", label: t("In Progress") },
      { value: "APPROVED", label: t("Approved") },
      { value: "COMPLETED", label: t("Completed") },
    ];
  }, [t]);

  const tableFilterTabs = useMemo(() => {
    return [
      {
        type: "radio_list",
        name: "analyst",
        title: "Analyst",
        icon: <i className="ri-user-line" />,
        isLoading: analystsAreFetching,
        defaultValue: tableFilters?.analyst,
        data: analysts || [],
        searchInListOption: {
          enabled: true,
          remote: false,
        },
        formatter: (item) => {
          return item.first_name + " " + item.last_name;
        },
      },
      {
        type: "check_list",
        name: "legal_entity_ids",
        title: "Connected Legal Entities",
        icon: <i className="ri-git-branch-line" />,
        isLoading: entitiesAreFetching,
        defaultValue: tableFilters?.legal_entity_ids,
        data: legalEntities?.legalEntities || [],
        searchInListOption: {
          enabled: true,
          remote: false,
        },
        formatter: (item) => {
          return item.title;
        },
      },
      {
        type: "check_list",
        name: "status",
        title: "Status",
        icon: <i className="ri-checkbox-circle-line" />,
        isLoading: false,
        defaultValue: tableFilters?.status,
        data: (statuses || []).map((item) => ({
          ...item,
          id: item.value,
        })),
        searchInListOption: {
          enabled: true,
          remote: false,
        },
        formatter: (item) => {
          return t(item.label);
        },
      },
      {
        type: "radio_list",
        name: "unassigned",
        title: "Unassigned TOMs",
        icon: <i className="ri-user-unfollow-line" />,
        isLoading: false,
        defaultValue: tableFilters?.unassigned,
        data: yesNoOptions,
        searchInListOption: {
          enabled: false,
          remote: false,
        },
        formatter: (item) => item.title,
      },
      {
        type: "radio_list",
        name: "revision_overdue",
        title: "Revision overdue",
        icon: <i className="ri-alarm-warning-line" />,
        isLoading: false,
        defaultValue: tableFilters?.revision_overdue,
        data: yesNoOptions,
        searchInListOption: {
          enabled: false,
          remote: false,
        },
        formatter: (item) => item.title,
      },
      {
        type: "date",
        name: "next_revision",
        title: "Next revision",
        icon: <i className="ri-calendar-line" />,
        defaultValue: tableFilters?.next_revision,
        options: {
          placeholder: t("Select date"),
        },
      },
      {
        type: "search_textbox",
        name: "text",
        title: "Search by title or section",
        icon: <i className="ri-terminal-box-line" />,
        defaultValue: tableFilters?.q,
        options: {
          placeholder: t("Search by title or section"),
          typingDelay: 750,
        },
        displayIndependently: true,
        onFilterChange: (value) => {
          setTableFilters((currentState) => ({ ...currentState, q: value }));
        },
      },
    ];
  }, [tableFilters, analysts, legalEntities, analystsAreFetching, entitiesAreFetching, t]);

  const onTableFilterOkButtonClicked = (filters, independentTabsData) => {
    if (filters) {
      const result = {};
      if ("analyst" in filters) {
        result.analyst =
          filters.analyst !== "all" ? filters.analyst : undefined;
      }

      if ("legal_entity_ids" in filters) {
        result.legal_entity_ids =
          filters.legal_entity_ids !== "all"
            ? filters.legal_entity_ids
            : undefined;
      }

      if ("status" in filters) {
        result.status = filters.status !== "all" ? filters.status : undefined;
      }

      if ("unassigned" in filters) {
        result.unassigned =
          filters.unassigned !== "all" ? filters.unassigned : undefined;
      }

      if ("revision_overdue" in filters) {
        result.revision_overdue =
          filters.revision_overdue !== "all"
            ? filters.revision_overdue
            : undefined;
      }

      if ("next_revision" in filters) {
        const value = filters.next_revision;

        // result.next_revision = value !== "all" ? value : undefined;

        if (value && value !== "all" && (value.from || value.to)) {
          result.next_revision_from = value.from
            ? new Date(value.from).toISOString().slice(0, 10)
            : undefined;

          result.next_revision_to = value.to
            ? new Date(value.to).toISOString().slice(0, 10)
            : undefined;
        } else {
          result.next_revision_from = undefined;
          result.next_revision_to = undefined;
        }
      }

      setTableFilters({
        ...tableFilters,
        ...result,
      });
    } else {
      setTableFilters({
        q: independentTabsData?.text,
        analyst: undefined,
        legal_entity_ids: undefined,
        status: undefined,
        unassigned: undefined,
        revision_overdue: undefined,
        next_revision: undefined,
      });
    }
  };

  useEffect(() => {
    onChange && onChange(tableFilters);
  }, [tableFilters, onChange]);

  return (
    <TableFilter
      className="me-2 controls-filter"
      defaultActiveTab="status"
      tabs={tableFilterTabs}
      onOkButtonClicked={onTableFilterOkButtonClicked}
    />
  );
};

export default withNamespaces()(FilteringComponent);
